/**
 * Global fetch wrapper that authenticates calls to the TIFPA backend.
 *
 * Every /api/* route on the backend requires
 * `Authorization: Bearer <supabase access token>`. Rather than threading the
 * token through every API module, this wraps window.fetch once at startup and
 * attaches the current session token to requests that target the backend
 * (VITE_API_BASE_URL, the production default, the local worker, or a
 * same-origin relative '/api/...' URL). Requests that already carry an
 * Authorization header are left alone, and third-party URLs (Supabase,
 * Google, etc.) are never touched.
 */

import { supabase } from './supabase';

/** Backend base URLs used across the portal's API modules. */
export function getBackendBases(): string[] {
  const bases = [
    import.meta.env.VITE_API_BASE_URL as string | undefined,
    'https://team.tifpaconsulting.com',
    'http://localhost:8787',
  ];
  if (typeof window !== 'undefined') bases.push(window.location.origin);
  return bases.filter((b): b is string => !!b);
}

/**
 * True when `url` points at a backend /api route under one of `bases`.
 * Relative URLs are resolved against `origin`.
 */
export function isBackendApiUrl(
  url: string,
  bases: string[],
  origin: string
): boolean {
  let resolved: URL;
  try {
    resolved = new URL(url, origin);
  } catch {
    return false;
  }
  const href = `${resolved.origin}${resolved.pathname}`;
  return bases.some(rawBase => {
    let base: URL;
    try {
      base = new URL(rawBase, origin);
    } catch {
      return false;
    }
    const basePath = base.pathname.replace(/\/+$/, '');
    const prefix = `${base.origin}${basePath}/api`;
    return href === prefix || href.startsWith(`${prefix}/`);
  });
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

let installed = false;

export function installApiAuthFetch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const url = requestUrl(input);
    if (!isBackendApiUrl(url, getBackendBases(), window.location.origin)) {
      return originalFetch(input, init);
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    if (headers.has('Authorization')) {
      return originalFetch(input, init);
    }

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        return originalFetch(input, { ...init, headers });
      }
    } catch {
      // No session available; send the request as-is and let the backend
      // reject it with 401.
    }
    return originalFetch(input, init);
  };
}
